import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import {
  Lock, Sparkles, CheckCircle2, RefreshCw, Loader2, X, Coins,
  ArrowRight, Target, Trophy, Download, Share2, FileText,
} from "lucide-react";
import { CFR_FULL, CFR_SHORT } from "../../lib/brand";
import CertificateModal from "./CertificateModal";
import ShareReportModal from "./ShareReportModal";
import VerifiedReadinessUpsell from "./VerifiedReadinessUpsell";
import { GetHelpLink } from "./SupportTicketModal";
import { useAnalysisRunner, AnalysisProgressModal, AnalysisProcessingCard, AnalysisSubmittedCard, phaseLabel } from "../../lib/analysisRunner";

export const READINESS_TARGET = 70; // Market Ready — matches backend MATCHMAKING_SCORE / emails / journey

/** Tiered readiness card:
 *  - Free tier: score + 1-sentence summary + "Unlock detailed report" CTA
 *  - Unlocked: full strengths/weaknesses/roadmap + personalised checklist
 *      + "Re-run analysis (1 credit)" with docs-updated gate.
 *
 *  Props:
 *    onContinue: () => void   (proceed to the founder dashboard)
 *    variant:    "onboarding" | "full"  (default "full").
 *      - "onboarding": hides strengths/weaknesses/funder matches/checklist
 *        and shows a "View detailed report" CTA that routes to the
 *        dedicated report page. Keeps score, headline, target banner and
 *        the onContinue button.
 *      - "full": full report (used on the dedicated /founder/readiness-report
 *        page and historically).
 */
export default function ReadinessReportCard({ onContinue, variant = "full" }) {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [buyOpen, setBuyOpen] = useState(false);
  const [rerunOpen, setRerunOpen] = useState(false);
  const [cert, setCert] = useState(null);  // {previousScore, newScore}
  const [company, setCompany] = useState(null);
  // Post-payment celebration shown right after Paystack confirms a credit purchase.
  const [purchaseCelebration, setPurchaseCelebration] = useState(null); // {creditsBalance}

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await api.get("/founder/readiness/report");
      setReport(data);
      try {
        const { data: c } = await api.get("/companies/me");
        setCompany(c);
      } catch (e) {
        // /companies/me failure is non-fatal — report card still renders.
        if (process.env.NODE_ENV !== "production") console.debug("[CCR] companies/me lookup skipped:", e?.message);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't load report");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    let cancelled = false;
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("reference") || params.get("trxref") || params.get("credits_session_id");
    const initialise = async () => {
      let purchasePaid = false;
      if (sid) {
        try {
          const { data } = await api.post(`/founder/credits/poll/${sid}`);
          // Show a post-payment celebration the moment the poll confirms the
          // purchase succeeded — closes the loop after a Paystack return
          // (otherwise the founder just lands on the dashboard with no signal
          // their payment actually went through).
          if (data?.payment_status === "paid") {
            purchasePaid = true;
          }
        } catch (e) {
          // Poll failure is rare and recoverable on next load; surface in dev only.
          if (process.env.NODE_ENV !== "production") console.debug("[CCR] credits poll failed:", e?.message);
        }
        window.history.replaceState({}, "", window.location.pathname);
      }
      try {
        const [r1, r2] = await Promise.all([
          api.get("/founder/readiness/report"),
          api.get("/companies/me").catch(() => ({ data: null })),
        ]);
        let reportData = r1.data;
        // Auto-heal: if the report still shows locked, try one idempotent
        // reconcile of the founder's recent Paystack tx. Same rescue path
        // as DetailedReport.jsx — safe to spam-call.
        if (reportData && reportData.unlocked === false) {
          try {
            const { data: rec } = await api.post("/founder/credits/reconcile");
            if (rec?.unlocked) {
              const refreshed = await api.get("/founder/readiness/report");
              reportData = refreshed.data;
              if (!purchasePaid && rec.fulfilled_now > 0) purchasePaid = true;
            }
          } catch (e) {
            if (process.env.NODE_ENV !== "production") console.debug("[CCR] auto-reconcile skipped:", e?.message);
          }
        }
        if (!cancelled) {
          setReport(reportData);
          if (r2.data) setCompany(r2.data);
          if (purchasePaid) {
            // Nudge AppShell header chip + the inline chip both refresh now.
            try { window.dispatchEvent(new Event("clinq:credits-changed")); } catch (_e) { /* ignore */ }
            setPurchaseCelebration({
              creditsBalance: reportData?.credits_balance ?? 0,
            });
          }
        }
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Couldn't load report");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    initialise();
    return () => { cancelled = true; };
  }, []);

  const toggleItem = async (item) => {
    try {
      const { data } = await api.post(
        `/founder/readiness/checklist/${item.id}/toggle`,
        { done: !item.done });
      setReport((r) => ({ ...r, checklist: data.checklist }));
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Couldn't update item");
    }
  };

  /** Re-display the most recent COMPLETED rerun result without charging a
   *  credit. Covers the "I ran a rerun and the modal never appeared because
   *  my browser tab crashed" case — the LLM call succeeded server-side,
   *  the credit was debited, and the founder shouldn't have to pay again
   *  to see the result they earned. */
  const showLastResult = async () => {
    try {
      const { data } = await api.get("/founder/readiness/last-rerun-result");
      const r = data?.result;
      if (!r) {
        window.alert("You haven't completed a re-run yet.");
        return;
      }
      if ((r.delta ?? 0) > 0) {
        setCert({
          previousScore: r.previous_score,
          newScore: r.new_score,
        });
      } else {
        window.alert(
          `Last re-run result:\n` +
          `Score: ${r.previous_score} → ${r.new_score} ` +
          `(${(r.delta ?? 0) >= 0 ? "+" : ""}${r.delta ?? 0}).\n` +
          `${r.credits_remaining ?? 0} re-run credit(s) left.`
        );
      }
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Couldn't load last result");
    }
  };

  // Render the purchase celebration overlay independently of the report
  // state. A founder returning from Paystack may briefly have no report
  // loaded (transient loading/error/null state) — without this hoist the
  // modal disappears behind the `if (!report) return null;` early return
  // and the founder gets no confirmation that their payment succeeded.
  const celebrationModal = purchaseCelebration ? (
    <PurchaseCelebrationModal
      creditsBalance={purchaseCelebration.creditsBalance}
      onClose={() => setPurchaseCelebration(null)}
      onRunRerun={() => {
        setPurchaseCelebration(null);
        // RerunModal needs a loaded report (checklist + balance). Only
        // surface the rerun launcher when we actually have one.
        if (report) setRerunOpen(true);
      }}
      canRunRerun={!!report}
    />
  ) : null;

  if (loading) return (
    <>
      {celebrationModal}
      <div className="mt-8 rounded-2xl bg-[#E5EEF1] p-6 text-sm text-neutral-600">
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin"/> Loading your Capital Readiness Report…
      </div>
    </>
  );
  if (err) return (
    <>
      {celebrationModal}
      <div className="mt-8 rounded-2xl bg-red-50 p-6 text-sm text-red-700">{err}</div>
    </>
  );
  if (!report) return celebrationModal;

  const score = report.readiness_score ?? 0;
  const tier = report.tier || {};

  return (
    <div className="mt-8 rounded-2xl bg-[#E5EEF1] p-6" data-testid="readiness-card">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:gap-4">
        <div className="text-5xl font-extrabold text-[#00828F]" data-testid="readiness-score">
          {score}
        </div>
        <div className="text-sm text-neutral-600">{CFR_SHORT} / 100</div>
      </div>
      <p className="mt-3 text-sm text-neutral-700" data-testid="readiness-headline">
        {report.unlocked ? report.summary : (report.headline || "Your readiness assessment is ready.")}
      </p>
      <p className="mt-1 text-[11px] uppercase tracking-widest text-neutral-400">
        {CFR_FULL}
      </p>

      {/* Target 85 banner — visible to everyone (locked + unlocked) */}
      <TargetBanner
        score={score}
        unlocked={!!report.unlocked}
        checklist={report.checklist || []}
      />

      {!report.unlocked ? (
        <FreeTierCTA tier={tier} onBuyClick={() => setBuyOpen(true)} />
      ) : variant === "onboarding" ? (
        <OnboardingUnlockedPreview onContinue={onContinue} />
      ) : (
        <UnlockedView
          report={report}
          onToggle={toggleItem}
          onRerunClick={() => setRerunOpen(true)}
          onContinue={onContinue}
          onBuyMoreClick={() => setBuyOpen(true)}
          onShowLastResult={showLastResult}
        />
      )}

      {buyOpen && (
        <BuyCreditsModal
          tier={tier}
          onClose={() => setBuyOpen(false)}
        />
      )}

      {rerunOpen && (
        <RerunModal
          checklist={report.checklist || []}
          balance={report.credits_balance}
          onClose={() => setRerunOpen(false)}
          onSuccess={(payload) => {
            setRerunOpen(false);
            setReport((r) => ({
              ...r,
              readiness_score: payload.new_score,
              summary: payload.analysis?.summary,
              strengths: payload.analysis?.strengths || [],
              weaknesses: payload.analysis?.weaknesses || [],
              roadmap: payload.analysis?.roadmap || [],
              checklist: payload.checklist,
              credits_balance: payload.credits_remaining,
            }));
            // Nudge AppShell to immediately re-fetch the credit balance so the
            // header chip shows the new value instantly (otherwise it lags up
            // to 30s waiting for the polling interval to fire).
            try {
              window.dispatchEvent(new Event("clinq:credits-changed"));
            } catch (_e) { /* ignore */ }
            // Show celebratory certificate when score went up
            if (payload.delta > 0) {
              setCert({
                previousScore: payload.previous_score,
                newScore: payload.new_score,
              });
            } else {
              window.alert(`Score went from ${payload.previous_score} → ${payload.new_score} ` +
                `(${payload.delta >= 0 ? "+" : ""}${payload.delta}). ` +
                `${payload.credits_remaining} re-run credit(s) left.`);
            }
          }}
        />
      )}

      {cert && (
        <CertificateModal
          open={true}
          onClose={() => setCert(null)}
          company={{
            name: company?.company_name,
            country: company?.country,
            sector: company?.sector,
          }}
          previousScore={cert.previousScore}
          newScore={cert.newScore}
        />
      )}

      {celebrationModal}
    </div>
  );
}

/** Post-payment success modal — confirms the purchase, shows the new credit
 *  balance, and offers a one-tap CTA to start running re-runs immediately. */
export function PurchaseCelebrationModal({ creditsBalance, onClose, onRunRerun, canRunRerun = true }) {
  return (
    <div
      data-testid="purchase-celebration-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/60 backdrop-blur-sm p-4"
    >
      <div className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="purchase-celebration-close"
          className="absolute right-3 top-3 rounded-md p-1.5 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="bg-gradient-to-br from-[#00828F] via-[#00647F] to-[#00303F] px-6 py-7 text-center text-white">
          <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-400/20 text-amber-300">
            <Trophy className="h-7 w-7" />
          </div>
          <p className="mt-3 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-300">
            Purchase confirmed
          </p>
          <h3 className="mt-1 text-2xl font-extrabold">You&apos;re Capital-Ready 🚀</h3>
          <p className="mt-2 text-sm text-white/80">
            Your credits are live and ready to use.
          </p>
        </div>
        <div className="px-6 py-6">
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-center">
            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700">
              Re-run credits available
            </p>
            <p className="mt-1 text-4xl font-extrabold text-amber-900" data-testid="purchase-celebration-balance">
              {creditsBalance}
            </p>
            <p className="mt-1 text-xs text-amber-900/80">
              Each credit lets you re-analyse your documents after updating them.
            </p>
          </div>
          <button
            onClick={onRunRerun}
            data-testid="purchase-celebration-cta"
            disabled={!canRunRerun}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#004B62] px-4 py-3 text-sm font-bold text-white hover:bg-[#00303F] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Sparkles className="h-4 w-4" /> Run your next analysis
          </button>
          <button
            onClick={onClose}
            data-testid="purchase-celebration-dismiss"
            className="mt-2 inline-flex w-full items-center justify-center rounded-lg border border-neutral-200 bg-white px-4 py-2.5 text-xs font-semibold text-neutral-600 hover:bg-neutral-50"
          >
            I&apos;ll do it later
          </button>
        </div>
      </div>
    </div>
  );
}

function OnboardingUnlockedPreview({ onContinue }) {
  const navigate = useNavigate();
  return (
    <div
      data-testid="readiness-onboarding-preview"
      className="mt-6 rounded-xl border border-[#00828F]/20 bg-white p-5"
    >
      <div className="flex items-start gap-3">
        <FileText className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#00828F]"/>
        <div className="flex-1">
          <p className="text-sm font-bold text-neutral-900">
            Your detailed Capital Readiness Report is ready
          </p>
          <p className="mt-1 text-sm text-neutral-600">
            Strengths, areas to improve, capital structure, funder-match shortlist
            and your personalised 90-day roadmap — all in one place. You can also
            download it as a PDF or share it via a private link.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              data-testid="readiness-view-detailed-report"
              onClick={() => navigate("/founder/readiness-report")}
              className="inline-flex items-center gap-2 rounded-lg bg-[#004B62] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#00303F]"
            >
              <Sparkles className="h-4 w-4"/>
              View my Capital Readiness Report
              <ArrowRight className="h-4 w-4"/>
            </button>
            {onContinue && (
              <button
                data-testid="step2-continue"
                onClick={onContinue}
                className="inline-flex items-center gap-2 rounded-lg border border-neutral-300 bg-white px-5 py-2.5 text-sm font-semibold text-neutral-800 hover:border-[#00828F] hover:text-[#00828F]"
              >
                View dashboard <ArrowRight className="h-4 w-4"/>
              </button>
            )}
          </div>
          <div className="mt-3">
            <GetHelpLink issueType="report_download" testid="report-preview-get-help" />
          </div>
        </div>
      </div>
    </div>
  );
}

function FreeTierCTA({ tier, onBuyClick }) {
  return (
    <div className="mt-6 rounded-xl border border-[#00828F]/20 bg-white p-5"
         data-testid="readiness-paywall">
      <div className="flex items-start gap-3">
        <Lock className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#00828F]"/>
        <div>
          <p className="text-sm font-bold text-neutral-900">
            Would you like to know exactly what to do to improve your Capital Readiness Score?
          </p>
          <p className="mt-1 text-sm text-neutral-600">
            Unlock a personalised action plan: a detailed analysis of where you&apos;re strong,
            where you&apos;re weak, and a step-by-step checklist your funders will love.
            Includes 3 re-runs as you make improvements.
          </p>
          <button
            data-testid="readiness-unlock-cta"
            onClick={onBuyClick}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#004B62] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#00303F]"
          >
            <Sparkles className="h-4 w-4"/>
            Get my Capital Readiness Report for KES {Number(tier.price_usd).toLocaleString()} ({tier.label})
            <ArrowRight className="h-4 w-4"/>
          </button>
          <p className="mt-2 text-[11px] text-neutral-500">
            Price tailored to your stage. One-time charge. Powered by Paystack.
          </p>
          <a href="/sample-report" target="_blank" rel="noopener noreferrer"
             data-testid="readiness-sample-link"
             className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#00828F] hover:underline">
            Not sure? See a sample report first →
          </a>
        </div>
      </div>
    </div>
  );
}

function TargetBanner({ score, unlocked, checklist }) {
  const target = READINESS_TARGET;
  const gap = Math.max(0, target - score);
  const reachedTarget = score >= target;
  const openItems = (checklist || []).filter((i) => !i.done).length;
  // Bar fills 0-100; we anchor the score and target labels at their real positions.
  const percent = Math.min(100, Math.max(0, score));

  return (
    <div
      data-testid="readiness-target-banner"
      className="mt-5 overflow-hidden rounded-2xl border border-[#00828F]/15 bg-gradient-to-br from-[#00303F] via-[#0A4543] to-[#00828F] p-5 text-white"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#4FB0BB]">
            {reachedTarget ? <Trophy className="h-3 w-3"/> : <Target className="h-3 w-3"/>}
            {reachedTarget ? "Market Ready threshold reached" : "Your target — Market Ready"}
          </p>
          <p className="mt-2 text-2xl font-extrabold leading-none">
            {reachedTarget ? (
              <>You&apos;re Funder-Ready 🎉</>
            ) : (
              <>{gap} <span className="text-base font-bold text-white/70">points to {target}</span></>
            )}
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-bold uppercase tracking-widest text-white/50">Target</p>
          <p className="text-3xl font-extrabold text-[#4FB0BB]">{target}<span className="text-base text-white/50">/100</span></p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="relative mt-4 h-2 w-full overflow-hidden rounded-full bg-white/10">
        <div
          className="h-full rounded-full bg-gradient-to-r from-[#4FB0BB] to-emerald-400 transition-all duration-700"
          style={{ width: `${percent}%` }}
          data-testid="readiness-target-progress"
        />
      </div>
      {/* Axis: 0 and 100 anchored, NOW/TARGET positioned proportionally */}
      <div className="relative mt-1 h-6 text-[10px] uppercase tracking-widest text-white/40">
        <span className="absolute left-0 top-0">0</span>
        <span className="absolute right-0 top-0">100</span>
        <span
          className="absolute top-0 -translate-x-1/2 font-semibold text-[#4FB0BB]"
          style={{ left: `${Math.min(100, Math.max(0, target))}%` }}
        >
          {target} target
        </span>
        <span
          className="absolute top-0 -translate-x-1/2 font-semibold text-white/80"
          style={{ left: `${Math.min(100, Math.max(0, score))}%` }}
        >
          {score} now
        </span>
      </div>

      {/* Rationale */}
      <p className="mt-4 text-xs leading-relaxed text-white/80">
        <b className="text-white">Why {target}?</b> {target} is the Market Ready line. Institutional funders see hundreds of decks every quarter, and our data shows
        founders scoring <b>{target}+</b> get materially higher response rates — your pitch deck,
        financials, legals and impact story all clear the bar that gets you taken seriously. At {target} Clinq starts introducing you to funders; below {target}, most
        funders politely pass without a meeting.
      </p>

      {/* CTA */}
      {!reachedTarget && (
        unlocked ? (
          openItems > 0 ? (
            <p className="mt-4 inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-xs font-semibold text-white">
              <CheckCircle2 className="h-4 w-4 text-[#4FB0BB]"/>
              {openItems} action item{openItems === 1 ? "" : "s"} below — tick them, update your docs, then re-analyse your documents to climb toward {target}.
            </p>
          ) : (
            <p className="mt-4 inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-xs font-semibold text-white">
              <RefreshCw className="h-4 w-4 text-[#4FB0BB]"/>
              You&apos;ve ticked everything — update your supporting documents and re-run your analysis to see your new Capital Readiness Score.
            </p>
          )
        ) : (
          <p className="mt-4 inline-flex items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-xs font-semibold text-white">
            <Sparkles className="h-4 w-4 text-[#4FB0BB]"/>
            Your full report includes the personalised checklist that gets you to {target}.
          </p>
        )
      )}
    </div>
  );
}

function UnlockedView({ report, onToggle, onRerunClick, onContinue, onBuyMoreClick, onShowLastResult }) {
  const [shareOpen, setShareOpen] = useState(false);
  const checklist = report.checklist || [];
  const doneCount = checklist.filter((i) => i.done).length;
  const totalCount = checklist.length || 1;
  const noCredits = (report.credits_balance || 0) === 0;

  // Group checklist by category
  const grouped = checklist.reduce((acc, it) => {
    (acc[it.category] = acc[it.category] || []).push(it); return acc;
  }, {});

  const verifiedStatus = (report.verified_readiness && report.verified_readiness.status) || "none";
  const rerunLocked = verifiedStatus === "pending" || verifiedStatus === "verified";

  return (
    <>
      {/* Verified Capital Readiness Score — upgrade / pending / verified */}
      <VerifiedReadinessUpsell
        verified={report.verified_readiness}
        indicativeScore={report.readiness_score ?? 0}
      />

      <div className="mt-6 grid gap-5 md:grid-cols-2">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Strengths</p>
          <ul className="mt-2 space-y-1.5">
            {(report.strengths || []).map((s, i) =>
              <li key={`s-${i}-${(s || "").slice(0, 24)}`} className="flex gap-2 text-sm"><span className="text-[#00828F]">✓</span>{s}</li>)}
          </ul>
        </div>
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">Areas to improve</p>
          <ul className="mt-2 space-y-1.5">
            {(report.weaknesses || []).map((s, i) =>
              <li key={`w-${i}-${(s || "").slice(0, 24)}`} className="flex gap-2 text-sm"><span className="text-amber-600">!</span>{s}</li>)}
          </ul>
        </div>
      </div>

      {/* Who's most likely to fund you */}
      {Array.isArray(report.funder_sources) && report.funder_sources.length > 0 && (
        <FunderSourcesPanel sources={report.funder_sources} />
      )}

      {/* Personalised checklist */}
      <div className="mt-8 rounded-xl border border-neutral-200 bg-white p-5" data-testid="readiness-checklist">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-bold text-neutral-900">Your action checklist</p>
            <p className="text-xs text-neutral-500">
              {doneCount} of {totalCount} complete · {Math.round((doneCount / totalCount) * 100)}%
            </p>
            <p className="mt-1 text-xs text-neutral-600">
              Completing these {totalCount} recommendations directly increases your Capital Readiness Score.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-900">
              <Coins className="h-3.5 w-3.5"/>
              {report.credits_balance} re-run credit{report.credits_balance === 1 ? "" : "s"}
            </span>
            <button
              onClick={onShowLastResult}
              data-testid="readiness-replay-last"
              title="Re-display your most recent re-run result (no credit charged)"
              className="hidden text-[11px] font-semibold text-neutral-500 underline-offset-2 hover:text-[#00828F] hover:underline sm:inline"
            >
              View last result
            </button>
            {rerunLocked ? (
              <span
                data-testid="readiness-rerun-locked"
                title="Re-run is locked while your Verified review is in progress or complete."
                className="inline-flex items-center gap-1.5 rounded-lg bg-neutral-200 px-3 py-1.5 text-xs font-bold text-neutral-500"
              >
                <RefreshCw className="h-3.5 w-3.5"/> Re-run locked
              </span>
            ) : noCredits ? (
              <button
                data-testid="readiness-buy-more"
                onClick={onBuyMoreClick}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#D1A330] px-3 py-1.5 text-xs font-bold text-neutral-950 hover:bg-[#8A6A1E]"
              >
                <Sparkles className="h-3.5 w-3.5"/> Buy more credits
              </button>
            ) : (
              <button
                data-testid="readiness-rerun-btn"
                onClick={onRerunClick}
                className="inline-flex items-center gap-1.5 rounded-lg bg-[#004B62] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#00303F]"
              >
                <RefreshCw className="h-3.5 w-3.5"/> Re-run my analysis (1 credit)
              </button>
            )}
          </div>
        </div>

        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-neutral-100">
          <div className="h-full bg-[#004B62] transition-all"
               style={{ width: `${(doneCount / totalCount) * 100}%` }}/>
        </div>

        <div className="mt-5 space-y-5">
          {Object.entries(grouped).map(([category, items]) => (
            <div key={category}>
              <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">
                {category}
              </p>
              <ul className="mt-2 space-y-2">
                {items.map((it) => (
                  <li key={it.id} className="flex items-start gap-3 rounded-lg border border-neutral-100 p-3 hover:bg-neutral-50"
                      data-testid={`checklist-item-${it.id}`}>
                    <button
                      onClick={() => onToggle(it)}
                      data-testid={`checklist-toggle-${it.id}`}
                      className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 ${
                        it.done ? "border-[#00828F] bg-[#004B62]" : "border-neutral-300 bg-white hover:border-[#00828F]"
                      }`}>
                      {it.done && <CheckCircle2 className="h-3.5 w-3.5 text-white"/>}
                    </button>
                    <div className={`flex-1 ${it.done ? "opacity-60" : ""}`}>
                      <p className={`text-sm font-semibold text-neutral-900 ${it.done ? "line-through" : ""}`}>
                        {it.title}
                      </p>
                      <p className="mt-0.5 text-xs text-neutral-600">{it.detail}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
        <GetHelpLink issueType="report_download" testid="report-card-get-help" className="mr-auto" />
        <button
          data-testid="readiness-share-report"
          onClick={() => setShareOpen(true)}
          className="inline-flex items-center gap-2 rounded-lg border border-neutral-300 bg-white px-5 py-2.5 text-sm font-semibold text-neutral-800 hover:border-[#00828F] hover:text-[#00828F]"
        >
          <Share2 className="h-4 w-4"/> Share report
        </button>
        <button
          data-testid="readiness-download-pdf"
          onClick={async () => {
            try {
              const res = await api.get("/founder/readiness/report.pdf", { responseType: "blob" });
              const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
              const a = document.createElement("a");
              a.href = url;
              a.download = "clinq-ccr-report.pdf";
              document.body.appendChild(a); a.click(); a.remove();
              setTimeout(() => URL.revokeObjectURL(url), 5000);
            } catch (e) {
              window.alert(e?.response?.data?.detail || "Couldn't download report.");
            }
          }}
          className="inline-flex items-center gap-2 rounded-lg border border-neutral-300 bg-white px-5 py-2.5 text-sm font-semibold text-neutral-800 hover:border-[#00828F] hover:text-[#00828F]"
        >
          <Download className="h-4 w-4"/> Download PDF
        </button>
        {onContinue && (
          <button
            data-testid="step2-continue"
            onClick={onContinue}
            className="inline-flex items-center gap-2 rounded-lg bg-[#004B62] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#00303F]">
            View dashboard <ArrowRight className="h-4 w-4"/>
          </button>
        )}
      </div>

      {shareOpen && (
        <ShareReportModal
          score={report.readiness_score ?? 0}
          onClose={() => setShareOpen(false)}
        />
      )}
    </>
  );
}

export function BuyCreditsModal({ tier, onClose, returnTo }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponErr, setCouponErr] = useState("");
  const [couponBusy, setCouponBusy] = useState(false);
  const finalPrice = coupon ? coupon.final : Number(tier.price_usd);

  const applyCoupon = async () => {
    setCouponBusy(true); setCouponErr("");
    try {
      const { data } = await api.post("/founder/credits/coupon/validate", { code: couponInput });
      setCoupon(data);
    } catch (e) {
      setCoupon(null); setCouponErr(e?.response?.data?.detail || "Invalid coupon");
    } finally { setCouponBusy(false); }
  };

  const goCheckout = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/founder/credits/checkout",
        { origin_url: window.location.origin, ...(returnTo ? { return_to: returnTo } : {}),
          ...(coupon ? { coupon_code: coupon.code } : {}) });
      window.location.href = data.url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
         data-testid="readiness-buy-modal">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between">
          <h3 className="text-lg font-bold text-neutral-900">{returnTo ? "Get your Capital Readiness Report" : "Get your Capital Readiness Report"}</h3>
          <button onClick={onClose} className="rounded-md p-1 text-neutral-400 hover:bg-neutral-100">
            <X className="h-5 w-5"/>
          </button>
        </div>
        <div className="mt-4 rounded-xl border border-[#00828F]/30 bg-[#E5EEF1] p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-[#00828F]">{tier.label} pack</p>
          <p className="mt-1 text-3xl font-extrabold text-neutral-900" data-testid="readiness-buy-price">
            {coupon && <span className="mr-2 text-lg font-semibold text-neutral-400 line-through">KES {Number(tier.price_usd).toLocaleString()}</span>}
            {finalPrice <= 0 ? "Free" : `KES ${Number(finalPrice).toLocaleString()}`}
          </p>
          {coupon && <p className="mt-1 text-xs font-bold text-[#00828F]" data-testid="readiness-coupon-applied">Coupon {coupon.code} applied — {coupon.percent}% off</p>}
          <ul className="mt-3 space-y-1.5 text-sm text-neutral-700">
            <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#00828F]"/> Detailed Capital Readiness analysis</li>
            <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#00828F]"/> Personalised action checklist</li>
            <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#00828F]"/> {tier.reruns} re-runs as you improve</li>
          </ul>
        </div>
        <div className="mt-3">
          {!couponOpen && !coupon ? (
            <button type="button" onClick={() => setCouponOpen(true)} className="text-xs font-semibold text-[#00828F] hover:underline" data-testid="readiness-coupon-toggle">
              Have a coupon code?
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <input value={couponInput} onChange={(e) => { setCouponInput(e.target.value.toUpperCase()); setCoupon(null); setCouponErr(""); }}
                placeholder="Enter coupon code" disabled={!!coupon}
                className="flex-1 rounded-lg border border-neutral-300 px-3 py-2 text-sm uppercase tracking-wide outline-none focus:border-[#00828F] disabled:bg-neutral-50"
                data-testid="readiness-coupon-input" />
              {coupon ? (
                <button type="button" onClick={() => { setCoupon(null); setCouponInput(""); }} className="text-xs font-semibold text-neutral-500 hover:text-neutral-800" data-testid="readiness-coupon-remove">Remove</button>
              ) : (
                <button type="button" onClick={applyCoupon} disabled={couponBusy || couponInput.trim().length < 3}
                  className="rounded-lg border border-[#00828F] px-3 py-2 text-xs font-bold text-[#00828F] hover:bg-[#E5EEF1] disabled:opacity-40" data-testid="readiness-coupon-apply">
                  {couponBusy ? "Checking…" : "Apply"}
                </button>
              )}
            </div>
          )}
          {couponErr && <p className="mt-1.5 text-xs text-red-600" data-testid="readiness-coupon-error">{couponErr}</p>}
        </div>
        {err && <p className="mt-3 text-sm text-red-600">{err}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg bg-neutral-100 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-200">
            Cancel
          </button>
          <button
            onClick={goCheckout}
            disabled={busy}
            data-testid="readiness-buy-confirm"
            className="inline-flex items-center gap-2 rounded-lg bg-[#004B62] px-5 py-2 text-sm font-bold text-white hover:bg-[#00303F] disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin"/> : <Sparkles className="h-4 w-4"/>}
            {busy ? (finalPrice <= 0 ? "Unlocking…" : "Redirecting…") : (finalPrice <= 0 ? "Unlock my report free" : "Continue to checkout")}
          </button>
        </div>
        <a href="/sample-report" target="_blank" rel="noopener noreferrer"
           data-testid="readiness-modal-sample-link"
           className="mt-3 block text-center text-[11px] font-semibold text-[#00828F] hover:underline">
          See a sample report first ↗
        </a>
        <div className="mt-4 border-t border-neutral-100 pt-3 text-center" data-testid="readiness-modal-advisor-option">
          <p className="text-[11px] text-neutral-500">Would you rather speak to an advisor for an in-depth business review instead?</p>
          <button type="button"
            onClick={() => { onClose(); window.location.assign("/discovery"); }}
            data-testid="readiness-modal-advisor-link"
            className="mt-1 text-[11px] font-semibold text-neutral-600 underline-offset-2 hover:text-[#00828F] hover:underline">
            Book a session with an advisor
          </button>
        </div>
      </div>
    </div>
  );
}

export function RerunModal({ checklist, balance, onClose, onSuccess }) {
  const ticked = checklist.filter((i) => i.done);
  const [confirmed, setConfirmed] = useState(false);
  const [err, setErr] = useState("");

  const analyzer = useAnalysisRunner({
    startEndpoint: "/founder/readiness/rerun",
    onDone: (payload) => {
      // The credits rerun returns one of two shapes:
      //  • unchanged-docs short-circuit (sync) — has `unchanged: true`
      //  • completed background job — has new_score / delta / credits_remaining via status endpoint
      onSuccess(payload);
    },
    onError: (msg) => setErr(msg),
  });

  const submit = () => {
    setErr("");
    analyzer.run({
      confirmed_doc_updates: true,
      confirmed_item_ids: ticked.map((i) => i.id),
    });
  };

  // Auto re-run: while the "still reading your documents" card is showing,
  // poll /processing-status every 5s and fire the re-run again (once) the
  // moment the backend reports no documents left to read.
  const [autoRetrying, setAutoRetrying] = useState(false);
  const autoRetriedRef = useRef(false);
  useEffect(() => {
    if (analyzer.processing) setAutoRetrying(false);
    if (!analyzer.processing || autoRetriedRef.current) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/processing-status");
        if (cancelled) return;
        if ((data?.docs_processing || 0) === 0) {
          autoRetriedRef.current = true;
          setAutoRetrying(true);
          try { window.dispatchEvent(new Event("clinq:processing-refresh")); } catch (_e) { /* ignore */ }
          setTimeout(() => { if (!cancelled) submit(); }, 800);
          return;
        }
      } catch (_) { /* keep polling */ }
      if (!cancelled) t = setTimeout(tick, 5000);
    };
    let t = setTimeout(tick, 5000);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyzer.processing]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
         data-testid="readiness-rerun-modal">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between">
          <h3 className="text-lg font-bold text-neutral-900">Re-analyse my Documents</h3>
          <button onClick={onClose} className="rounded-md p-1 text-neutral-400 hover:bg-neutral-100">
            <X className="h-5 w-5"/>
          </button>
        </div>
        <p className="mt-2 text-sm text-neutral-600">
          This will use <b>1 credit</b> ({balance - 1} remaining after).
          Before re-running, confirm you&apos;ve uploaded any additional supporting documents.
        </p>
        {ticked.length > 0 && (
          <div className="mt-4 rounded-xl border border-neutral-200 bg-neutral-50 p-3">
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-500">
              You ticked these {ticked.length} item{ticked.length === 1 ? "" : "s"}:
            </p>
            <ul className="mt-2 space-y-1 text-sm text-neutral-700">
              {ticked.map((i) => (
                <li key={i.id} className="flex gap-2">
                  <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-[#00828F]"/> {i.title}
                </li>
              ))}
            </ul>
          </div>
        )}
        <label className="mt-4 flex cursor-pointer items-start gap-2 text-sm text-neutral-700"
               data-testid="readiness-rerun-confirm-checkbox">
          <input type="checkbox" className="mt-1 h-4 w-4" checked={confirmed}
                 onChange={(e) => setConfirmed(e.target.checked)}/>
          <span>
            I confirm I&apos;ve uploaded additional supporting documents to my application.
          </span>
        </label>
        {err && <p className="mt-3 text-sm text-red-600">{err}</p>}
        {analyzer.processing && <div className="mt-4"><AnalysisProcessingCard info={analyzer.processing} retrying={autoRetrying} testIdPrefix="rerun-analysis" /></div>}
        {analyzer.submitted && <div className="mt-4"><AnalysisSubmittedCard testIdPrefix="rerun-analysis" nextStep="your new score" /></div>}
        <AnalysisProgressModal busy={analyzer.busy} phase={analyzer.phase} testIdPrefix="rerun-analysis" />
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} disabled={analyzer.busy}
            className="rounded-lg bg-neutral-100 px-4 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-200 disabled:opacity-50">
            {analyzer.processing || analyzer.submitted ? "Close" : "Cancel"}
          </button>
          {!analyzer.processing && !analyzer.submitted && (
          <button
            onClick={submit}
            disabled={!confirmed || analyzer.busy}
            data-testid="readiness-rerun-submit"
            className="inline-flex items-center gap-2 rounded-lg bg-[#004B62] px-5 py-2 text-sm font-bold text-white hover:bg-[#00303F] disabled:opacity-50">
            {analyzer.busy ? <Loader2 className="h-4 w-4 animate-spin"/> : <RefreshCw className="h-4 w-4"/>}
            {analyzer.busy ? `${phaseLabel(analyzer.phase)}…` : "Re-run my analysis (1 credit)"}
          </button>
          )}
        </div>
      </div>
    </div>
  );
}

const _LIKELIHOOD_BADGE = {
  high:   { label: "High",   bg: "bg-emerald-100", text: "text-emerald-800", dot: "bg-emerald-500" },
  medium: { label: "Medium", bg: "bg-amber-100",   text: "text-amber-800",   dot: "bg-amber-500" },
  low:    { label: "Low",    bg: "bg-neutral-100", text: "text-neutral-600", dot: "bg-neutral-400" },
};

function FunderSourcesPanel({ sources }) {
  const order = { high: 0, medium: 1, low: 2 };
  const sorted = [...sources].sort((a, b) => order[a.likelihood] - order[b.likelihood]);
  return (
    <div className="mt-8 rounded-xl border border-neutral-200 bg-white p-5"
         data-testid="funder-sources-panel">
      <p className="text-sm font-bold text-neutral-900">Who&apos;s most likely to fund you</p>
      <p className="mt-1 text-xs text-neutral-500">
        Based on your stage, sector, revenue and CCR Score&trade;. Spend your outreach time where the likelihood is High.
      </p>
      <ul className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {sorted.map((s) => {
          const b = _LIKELIHOOD_BADGE[s.likelihood] || _LIKELIHOOD_BADGE.low;
          return (
            <li key={s.id} data-testid={`funder-source-${s.id}`}
                className="rounded-lg border border-neutral-100 p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-bold text-neutral-900">{s.label}</p>
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${b.bg} ${b.text}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${b.dot}`} /> {b.label}
                </span>
              </div>
              <p className="mt-1 text-xs text-neutral-600">{s.blurb}</p>
              {s.rationale && (
                <p className="mt-1.5 text-[11px] italic text-neutral-500">{s.rationale}</p>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
